/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../../locales/en-US/descriptors';
import {
  formattedDate,
  getFirstDayofSelectedMonth,
  isObjectOrArrayAndEmpty
} from '../../../../shared/Utils/Utils';
import useConnectorAndDynamicProps from '../../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import ConnectionError from '../../../../shared/ErrorComponents/ConnectionError';
import withMSLoadingStatus from '../../../../shared/MSStatusWrappers/MSLoadingStatus';
import withMSErrorStatus from '../../../../shared/MSStatusWrappers/MSErrorStatus';
import useMounted from '../../../../shared/useMount/useMount';
import UtilityTabComponent from '../SiteUtilityComponent/SiteUtilityComponent';
import { apiNoContentStatusCode } from '../../../../shared/Utils/Constants';
import { ISiteCard, IUtilityBillData } from '../../../IEnergyManagementProps';
import { getCarbonEndDateBasedOnSub } from '../../../../shared/Utils/CarbonViewDetailsUtils';
import { getConnctorConfig } from '../../../../shared/Utils/DataConnectorUtils';
import IUtilityBillComponentProps from '../../../UtilityBillWidget/UtilityBillComponent/IUtilityBillComponentProps';

interface IUtilityBillHOCContent extends ISiteCard {
  data: IUtilityBillData;
  displayBaseline?: boolean;
}

const SiteUtilityHOCContent = (props: IUtilityBillHOCContent) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, displayBaseline } = props;

  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  return (
    <div>
      <UtilityTabComponent data={data} displayBaseline={displayBaseline} />
    </div>
  );
};

const UtilityTabHOCWithLoader = withMSLoadingStatus(
  withMSErrorStatus(SiteUtilityHOCContent)
);

const SiteUtilityHOC = (props: IUtilityBillComponentProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { bearerToken, requestContext, displayBaseline } = props;
  const startTime = getFirstDayofSelectedMonth(
    requestContext.context.startTime!
  );
  const endTime = getCarbonEndDateBasedOnSub(
    requestContext.context.subscriptionType!,
    requestContext.context.endTime!,
    requestContext.context.startTime!
  );

  const endTimeNewFormat = formattedDate(endTime);

  const connectorConfig = getConnctorConfig(
    'utility-bill',
    requestContext,
    startTime,
    endTimeNewFormat
  );

  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  const onRefresh = () => dataConnector?.actions?.getAll();

  useEffect(() => {
    if (isMounted) {
      onRefresh();
    }
  }, [requestContext]);

  const { all, loading, error, status } = dataConnector.data;
  const UtilitySiteBill = (all as unknown) as IUtilityBillData;
  const isDataAvailable = UtilitySiteBill ? true : false;
  const [errorInfo, setErrorInfo] = useState(error);
  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [data, setData] = useState({} as IUtilityBillData);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(UtilitySiteBill);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  return (
    <UtilityTabHOCWithLoader
      loading={isLoading}
      error={errorInfo}
      statusCode={statusCode}
      onRefresh={onRefresh}
      data={data}
      loadingText={t(d.LOADING)}
      title={t(d.CONNECTIONERRORTITLE)}
      noDataMessage={t(d.NODATAAVAILABLE)}
      connectionErrorMessage={t(d.CONNECTIONERROR)}
      isNoContent={status === apiNoContentStatusCode}
      displayBaseline={displayBaseline}
    />
  );
};
export default SiteUtilityHOC;
